import { useEffect, useRef, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";

const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const stats = [
  { value: 125, label: "Creative Members" },
  // { value: 1020, label: "Overseas Operations" },
  { value: 10000, label: "Domestic Operations", suffix: "K" },
  { value: 125, label: "Creative Ideas" },
];

const locations = [
  {
    id: "odisha",
    coordinates: [85.8245, 20.2961], // Bhubaneswar
    active: true,
    address: "Bhubaneswar\nOdisha",
    country: "India",
    cardX: -160,
    cardY: -10,
  },
  {
    id: "chhattisgarh",
    coordinates: [81.6296, 21.2514], // Raipur
    active: true,
    address: "Raipur\nChhattisgarh",
    country: "India",
    cardX: -140,
    cardY: 20,
  },
  {
    id: "rajasthan",
    coordinates: [75.7873, 26.9124], // Jaipur
    active: true,
    address: "Jaipur\nRajasthan",
    country: "India",
    cardX: -120,
    cardY: -20,
  },
  {
    id: "bihar",
    coordinates: [85.1376, 25.5941], // Patna
    active: true,
    address: "Patna\nBihar",
    country: "India",
    cardX: -100,
    cardY: -15,
  },
  {
    id: "maharashtra",
    coordinates: [72.8777, 19.076], // Mumbai
    active: true,
    address: "Mumbai\nMaharashtra",
    country: "India",
    cardX: -130,
    cardY: 25,
  },

  // optional inactive markers
  { id: "india-center", coordinates: [82.8, 22.5], active: false },
];

export default function StatsMap() {
  const [counts, setCounts] = useState(stats.map(() => 0));
  const [started, setStarted] = useState(false);
  const ref = useRef(null);

  // 👇 Intersection Observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
          startCounting();
        }
      },
      { threshold: 0.3 },
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  // 👇 Counter Logic
  const startCounting = () => {
    stats.forEach((stat, index) => {
      let start = 0;
      const end = stat.value;
      const duration = 2000;

      const stepTime = 16;
      const step = Math.ceil(end / (duration / stepTime));

      const timer = setInterval(() => {
        start += step;

        if (start >= end) {
          start = end;
          clearInterval(timer);
        }

        setCounts((prev) => {
          const updated = [...prev];
          updated[index] = start;
          return updated;
        });
      }, stepTime);
    });
  };

  return (
    <div
      ref={ref}
      className="w-full flex items-center px-4 sm:px-8 py-12"
      style={{ backgroundColor: "#f5f0e8", minHeight: "100vh" }}
    >
      <div className="w-full max-w-7xl mx-auto  flex flex-col lg:flex-row items-center gap-2">
        {/* ── Stats Column ── */}
        <div className="flex flex-col gap-8 min-w-[210px] w-full lg:w-auto flex-shrink-0">
          {stats.map((stat, i) => (
            <div key={i}>
              <p
                className="text-5xl font-black leading-none text-gray-900"
                style={{
                  fontFamily: "'Arial Black', Arial, sans-serif",
                  letterSpacing: "-0.02em",
                }}
              >
                {stat.suffix ? `${Math.floor(counts[i] / 1000)}K` : counts[i]}+
              </p>

              <p className="text-sm font-bold text-gray-800 mt-1">
                {stat.label}
              </p>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div
          className="hidden lg:block self-stretch flex-shrink-0 mx-8"
          style={{ width: "1px", background: "#c9bfb0", minHeight: "500px" }}
        />

        {/* ── Map Column ── */}
        <div className="flex-1 w-full">
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 1000,
              center: [82.8, 22.5],
            }}
            style={{ width: "100%", height: "450px" }}
          >
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies
                  .filter((geo) => geo.properties.name === "India")
                  .map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="#f8f5f2"
                      stroke="#c4bab0"
                      strokeWidth={0.8}
                    />
                  ))
              }
            </Geographies>

            {locations.map((loc) =>
              loc.active ? (
                <Marker key={loc.id} coordinates={loc.coordinates}>
                  <circle
                    r={9}
                    fill="none"
                    stroke="#ef4444"
                    strokeWidth={1.5}
                    opacity={0.35}
                  />
                  <circle r={5} fill="#ef4444" />

                  <foreignObject
                    x={loc.cardX}
                    y={loc.cardY}
                    width="178"
                    height="88"
                    style={{ overflow: "visible", pointerEvents: "none" }}
                  >
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      style={{
                        background: "white",
                        borderRadius: "14px",
                        padding: "12px 16px",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.09)",
                        border: "1px solid #e8e0d5",
                        width: "170px",
                      }}
                    >
                      <p
                        style={{
                          fontSize: "12.5px",
                          color: "#888",
                          whiteSpace: "pre-line",
                          margin: 0,
                        }}
                      >
                        {loc.address}
                      </p>
                      {/* <p
                        style={{
                          fontSize: "15px",
                          fontWeight: "900",
                          marginTop: 5,
                        }}
                      >
                        {loc.country}
                      </p> */}
                    </div>
                  </foreignObject>
                </Marker>
              ) : (
                <Marker key={loc.id} coordinates={loc.coordinates}>
                  <circle r={3.5} fill="#1a1a1a" />
                </Marker>
              ),
            )}
          </ComposableMap>
        </div>
      </div>
    </div>
  );
}
